import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import haukeApiService from '../services/haukeApi';
import '../assets/css/tasks.css';

const Tasks = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('today');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [stats, setStats] = useState({ total: 0, pending: 0, completed: 0, overdue: 0, today: 0 });

  useEffect(() => {
    loadTasks();
  }, [activeTab, searchTerm, filterPriority, filterStatus, filterDate]);

  useEffect(() => {
    loadStats();
  }, []);

  const loadTasks = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Parameter für serverseitiges Filtern vorbereiten
      const todayIso = new Date().toISOString().split('T')[0];
      let derivedStatus = '';
      let derivedDateFrom = '';
      let derivedDateTo = '';

      // Helper: Tagesbereich ISO (UTC)
      const toDayRange = (dateStr) => {
        try {
          const [y, m, d] = dateStr.split('-').map((v) => parseInt(v, 10));
          const start = new Date(Date.UTC(y, m - 1, d, 0, 0, 0, 0));
          const end = new Date(Date.UTC(y, m - 1, d, 23, 59, 59, 999));
          return { from: start.toISOString(), to: end.toISOString() };
        } catch (_) {
          return { from: '', to: '' };
        }
      };

      // Tab -> Server-Parameter
      switch (activeTab) {
        case 'pending':
          derivedStatus = 'pending';
          break;
        case 'completed':
          derivedStatus = 'completed';
          break;
        case 'overdue':
          derivedStatus = 'overdue';
          break;
        case 'today':
          derivedStatus = 'pending';
          const rangeToday = toDayRange(todayIso);
          derivedDateFrom = rangeToday.from;
          derivedDateTo = rangeToday.to;
          break;
        default:
          derivedStatus = '';
      }

      // Dropdown-Status nur anwenden, wenn Tab 'all' oder 'completed' aktiv ist
      if (filterStatus && (activeTab === 'all' || activeTab === 'completed')) {
        derivedStatus = filterStatus;
      }

      // Einzelnes Datum als Range interpretieren
      if (filterDate) {
        const range = toDayRange(filterDate);
        derivedDateFrom = range.from;
        derivedDateTo = range.to;
      }

      const params = {
        status: derivedStatus,
        priority: filterPriority || '',
        dateFrom: derivedDateFrom,
        dateTo: derivedDateTo,
        search: searchTerm || ''
      };

      const response = await haukeApiService.getAllTasks(params);
      setTasks(response.data || []);
      
    } catch (error) {
      console.error('Fehler beim Laden der Aufgaben:', error);
      setError(`Fehler beim Laden der Aufgaben: ${error.message}`);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await haukeApiService.getTasksStats();
      const s = response?.data || {};
      setStats({
        total: s.totalTasks || 0,
        pending: s.pendingTasks || 0,
        completed: s.completedTasks || 0,
        overdue: s.overdueTasks || 0,
        today: s.todayTasks || 0
      });
    } catch (e) {
      // Stats sind optional; bei Fehler keine Blockade
    }
  };

  // Statistiken kommen serverseitig

  const getFilteredTasks = () => tasks; // gesamte Filterung kommt vom Backend

  const handleToggleComplete = async (task) => {
    try {
      const updates = {
        completed: !task.completed,
        updatedAt: new Date().toISOString()
      };

      await haukeApiService.updateTask(task.hofId, task.id, updates);
      
      setTasks(prev => prev.map(t => 
        t.id === task.id ? { ...t, ...updates } : t
      ));
      
    } catch (error) {
      console.error('Fehler beim Aktualisieren der Aufgabe:', error);
      alert(`Fehler beim Aktualisieren der Aufgabe: ${error.message}`);
    }
  };

  const handleDeleteTask = async (task) => {
    if (!window.confirm(`Aufgabe "${task.text}" wirklich löschen?`)) {
      return;
    }

    try {
      await haukeApiService.deleteTask(task.hofId, task.id);
      setTasks(prev => prev.filter(t => t.id !== task.id));
      
    } catch (error) {
      console.error('Fehler beim Löschen der Aufgabe:', error);
      alert(`Fehler beim Löschen der Aufgabe: ${error.message}`);
    }
  };

  const handleTaskDoubleClick = (task) => {
    const hofId = task.hofId;
    if (hofId && hofId !== 'admin') {
      navigate(`/profil-admin/${hofId}?tab=notes`);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterPriority('');
    setFilterStatus('');
    setFilterDate('');
    setActiveTab('all');
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high': return 'priority_high';
      case 'urgent': return 'error';
      case 'low': return 'low_priority';
      default: return 'remove';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('de-DE');
  };

  const isOverdue = (task) => {
    if (!task.dueDate || task.completed) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(task.dueDate) < today;
  };

  // stats kommen aus loadStats()
  const filteredTasks = getFilteredTasks();
  const hasActiveFilters = searchTerm || filterPriority || filterStatus || filterDate || (activeTab !== 'all');

  return (
    <div className="tasks-container">
      {/* Header */}
      <div className="tasks-header">
        <h1>Aufgaben</h1>
      </div>

      {/* Tabs */}
      <div className="tasks-tabs">
        <button 
          className={`tab-button ${activeTab === 'today' ? 'active' : ''}`}
          onClick={() => setActiveTab('today')}
        >
          <i className="material-icons">today</i>
          Heute
          <span className="tab-badge">{stats.today}</span>
        </button>
        <button 
          className={`tab-button ${activeTab === 'overdue' ? 'active' : ''}`}
          onClick={() => setActiveTab('overdue')}
        >
          <i className="material-icons">warning</i>
          Überfällig
          <span className="tab-badge">{stats.overdue}</span>
        </button>
        <button 
          className={`tab-button ${activeTab === 'pending' ? 'active' : ''}`}
          onClick={() => setActiveTab('pending')}
        >
          <i className="material-icons">pending</i>
          Offen
          <span className="tab-badge">{stats.pending}</span>
        </button>
        <button 
          className={`tab-button ${activeTab === 'completed' ? 'active' : ''}`}
          onClick={() => setActiveTab('completed')}
        >
          <i className="material-icons">check_circle</i>
          Erledigt
          <span className="tab-badge">{stats.completed}</span>
        </button>
        <button 
          className={`tab-button ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          <i className="material-icons">view_list</i>
          Alle
          <span className="tab-badge">{stats.total}</span>
        </button>
      </div>

      {/* Filters */}
      <div className="tasks-filters">
        <div className="filter-row">
          <div className="filter-group">
            <input
              type="text"
              className="search-input"
              placeholder="Aufgaben durchsuchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <i className="material-icons search-icon">search</i>
          </div>

          <select
            className="filter-select"
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
          >
            <option value="">Alle Prioritäten</option>
            <option value="low">Niedrig</option>
            <option value="normal">Normal</option>
            <option value="high">Hoch</option>
            <option value="urgent">Dringend</option>
          </select>

          <select
            className="filter-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="">Alle Status</option>
            <option value="pending">Offen</option>
            <option value="completed">Erledigt</option>
          </select>

          <input
            type="date"
            className="filter-input"
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
          />

          {hasActiveFilters && (
            <button
              className="clear-filters-btn"
              onClick={clearFilters}
            >
              <i className="material-icons">clear</i>
              Filter löschen
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="loading-state">
          <i className="material-icons">hourglass_empty</i>
          <p>Lade Aufgaben...</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <i className="material-icons">error</i>
          <p>{error}</p>
        </div>
      ) : filteredTasks.length === 0 ? (
        <div className="empty-state">
          <i className="material-icons">task_alt</i>
          <h3>Keine Aufgaben gefunden</h3>
          <p>
            {hasActiveFilters 
              ? 'Versuchen Sie, Ihre Filter anzupassen.'
              : 'Erstellen Sie eine neue Aufgabe, um loszulegen.'
            }
          </p>
        </div>
      ) : (
        <div className="tasks-table-container">
          <table className="tasks-table">
            <thead>
              <tr>
                <th width="50">Status</th>
                <th>Aufgabe</th>
                <th width="120">Typ</th>
                <th width="100">Priorität</th>
                <th width="120">Fällig</th>
                <th width="180">Hof</th>
                <th width="100">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {filteredTasks.map(task => (
                <tr 
                  key={task.id}
                  className={`task-row ${task.completed ? 'completed' : ''} ${isOverdue(task) ? 'overdue' : ''}`}
                  onDoubleClick={() => handleTaskDoubleClick(task)}
                  title="Doppelklick zum Öffnen der Profil-Admin Seite"
                >
                  <td>
                    <div className="task-checkbox">
                      <input
                        type="checkbox"
                        checked={task.completed}
                        onChange={() => handleToggleComplete(task)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </td>
                  
                  <td>
                    <div className="task-title">{task.text}</div>
                  </td>
                  
                  <td>
                    <span className="task-type-badge">{task.type || '-'}</span>
                  </td>
                  
                  <td>
                    <div className="task-priority-cell">
                      <i 
                        className="material-icons"
                        title={`Priorität: ${task.priority || 'normal'}`}
                      >
                        {getPriorityIcon(task.priority)}
                      </i>
                      <span>{task.priority || 'normal'}</span>
                    </div>
                  </td>
                  
                  <td>
                    {task.dueDate ? formatDate(task.dueDate) : '-'}
                  </td>
                  
                  <td>
                    {task.direkterzeugerName && task.direkterzeugerName !== 'Admin' ? (
                      <div className="task-farm-cell">
                        <strong>{task.direkterzeugerName}</strong>
                        <br />
                        <small>{task.direkterzeugerCity || ''}</small>
                      </div>
                    ) : (
                      <span className="admin-task">Admin</span>
                    )}
                  </td>
                  
                  <td>
                    <div className="task-actions">
                      <button 
                        className="action-btn"
                        title="Bearbeiten"
                        onClick={(e) => {
                          e.stopPropagation();
                          console.log('Edit task:', task);
                        }}
                      >
                        <i className="material-icons">edit</i>
                      </button>
                      <button 
                        className="action-btn delete-btn"
                        title="Löschen"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteTask(task);
                        }}
                      >
                        <i className="material-icons">delete</i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Tasks; 